import { Star } from 'lucide-preact';

import { Redemptions } from '@/components/Redemptions';
import { useWidgetConfig } from '@/context/WidgetConfigContext';
import { calculateTierCompletionPercentage } from '@/helpers/functions';
import { lightenColor } from '@/helpers/functions/theme';
import type { I_AccordionItemProps } from '@/types/AccordionItem';
import { type T_CustomerLoyaltyBasicInfo, type T_LoyaltyTier } from '@/types/RegisteredCustomer';
import { Progress } from '@/ui/progress';

export const useLoyaltyTierAccordionItems = (
  loyaltyTier?: T_LoyaltyTier,
  basicInfo?: T_CustomerLoyaltyBasicInfo,
): I_AccordionItemProps[] => {
  if (!loyaltyTier || !basicInfo) return [];

  const {
    currentTier,
    nextTier,
    redemptions,
    remainingRequirementsUpperTier,
    benefitsDescription,
  } = loyaltyTier;

  const TIER_PROGRESS = calculateTierCompletionPercentage(loyaltyTier, basicInfo);

  const { theme } = useWidgetConfig();
  const iconColor = theme?.headingColor;
  const lighterBackground = lightenColor(iconColor ?? '#FFF7ED', 90);

  return [
    {
      value: 'tiers',
      triggerContent: (
        <div className='flex items-center space-x-3'>
          <div
            className='flex min-h-10 min-w-10 items-center justify-center rounded-full'
            style={{ backgroundColor: lighterBackground }}
          >
            <Star className='min-h-1 min-w-1 p-1' style={{ color: iconColor }} />
          </div>
          <div className='text-left'>
            <h3>Your Tier: {currentTier && currentTier?.nameEn.toUpperCase()}</h3>
            <h5>{benefitsDescription}</h5>
          </div>
        </div>
      ),
      children: (
        <div className='space-y-4'>
          {nextTier && (
            <div>
              <div className='mb-2 flex items-start justify-start gap-1'>
                <span className='min-h-[1rem] text-sm'>Progress </span>{' '}
                <span className='text-sm font-medium'>
                  <strong> {remainingRequirementsUpperTier}</strong>
                </span>
              </div>
              <Progress value={TIER_PROGRESS} className='h-2' />
            </div>
          )}
          <Redemptions redemptions={redemptions} showRemainingUsages={true} />
        </div>
      ),
    },
  ];
};
